import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { tradingAPI } from '@/lib/api';
import { Clock, TrendingUp, TrendingDown } from 'lucide-react';

const formatCurrency = (v) => v == null ? '$0.00' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);

export default function TradeHistoryPage() {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    tradingAPI.getAllTrades(100).then(r => { setTrades(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const filtered = filter === 'all' ? trades : trades.filter(t => t.status === filter);
  const totalPnl = trades.filter(t => t.status === 'closed').reduce((s, t) => s + (t.profit_loss || 0), 0);
  const winCount = trades.filter(t => t.status === 'closed' && (t.profit_loss || 0) > 0).length;
  const closedCount = trades.filter(t => t.status === 'closed').length;
  const winRate = closedCount > 0 ? ((winCount / closedCount) * 100).toFixed(1) : '0.0';

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="trade-history-page">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded bg-green-500/15 flex items-center justify-center">
            <Clock className="w-5 h-5 text-green-400" />
          </div>
          <div>
            <h1 className="text-lg font-semibold font-['Outfit'] text-[var(--text-primary)]">Trade History</h1>
            <p className="text-xs text-[var(--text-secondary)]">All your past and current trades</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="p-4 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded">
            <span className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">Total Trades</span>
            <p className="text-xl font-bold font-mono text-[var(--text-primary)] mt-1">{trades.length}</p>
          </div>
          <div className="p-4 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded">
            <span className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">Win Rate</span>
            <p className="text-xl font-bold font-mono text-emerald-400 mt-1">{winRate}%</p>
          </div>
          <div className="p-4 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded">
            <span className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">Total P/L</span>
            <p className={`text-xl font-bold font-mono mt-1 ${totalPnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {totalPnl >= 0 ? '+' : ''}{formatCurrency(totalPnl)}
            </p>
          </div>
          <div className="p-4 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded">
            <span className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">Open Positions</span>
            <p className="text-xl font-bold font-mono text-green-400 mt-1">{trades.filter(t => t.status === 'open').length}</p>
          </div>
        </div>

        {/* Filter */}
        <div className="flex gap-2">
          {['all', 'open', 'closed'].map(f => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
              className={`px-4 py-1.5 rounded text-xs font-medium transition-colors ${filter === f ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'text-[var(--text-secondary)] hover:text-white bg-[var(--hover-bg)] border border-[var(--border-subtle)]'}`}>
              {f.charAt(0).toUpperCase() + f.slice(1)} {f === 'all' ? `(${trades.length})` : `(${trades.filter(t => t.status === f).length})`}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded overflow-x-auto" data-testid="history-table">
          {loading ? (
            <div className="p-8 text-center text-sm text-[var(--text-secondary)]">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-[var(--text-secondary)]">No trades found</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-[var(--text-secondary)] uppercase border-b border-[var(--border-subtle)]">
                  <th className="text-left px-5 py-3">Pair</th>
                  <th className="text-left px-3 py-3">Type</th>
                  <th className="text-left px-3 py-3">Status</th>
                  <th className="text-right px-3 py-3">Volume</th>
                  <th className="text-right px-3 py-3">Entry</th>
                  <th className="text-right px-3 py-3">Close</th>
                  <th className="text-right px-3 py-3">Target</th>
                  <th className="text-right px-3 py-3">SL</th>
                  <th className="text-right px-3 py-3">Spread</th>
                  <th className="text-right px-3 py-3">P/L</th>
                  <th className="text-right px-5 py-3">Opened</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(t => (
                  <tr key={t.id} className="border-b border-[var(--border-subtle)] hover:bg-[var(--hover-bg)]">
                    <td className="px-5 py-3 font-mono text-[var(--text-primary)] text-xs">{t.symbol?.toUpperCase()}</td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${t.type === 'buy' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
                        {t.type === 'buy' ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                        {t.type?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-3 py-3"><span className={`text-xs ${t.status === 'open' ? 'text-green-400' : 'text-[var(--text-secondary)]'}`}>{t.status}</span></td>
                    <td className="px-3 py-3 text-right font-mono text-[var(--text-secondary)] text-xs">{t.volume}</td>
                    <td className="px-3 py-3 text-right font-mono text-[var(--text-secondary)] text-xs">{formatCurrency(t.entry_price)}</td>
                    <td className="px-3 py-3 text-right font-mono text-[var(--text-secondary)] text-xs">{t.status === 'closed' ? formatCurrency(t.current_price) : '-'}</td>
                    <td className="px-3 py-3 text-right font-mono text-emerald-400/70 text-xs">{t.take_profit ? formatCurrency(t.take_profit) : '-'}</td>
                    <td className="px-3 py-3 text-right font-mono text-red-400/70 text-xs">{t.stop_loss ? formatCurrency(t.stop_loss) : '-'}</td>
                    <td className="px-3 py-3 text-right font-mono text-amber-400 text-xs">{t.spread_amount ? formatCurrency(t.spread_amount) : '-'}</td>
                    <td className={`px-3 py-3 text-right font-mono text-xs font-medium ${(t.profit_loss || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {t.status === 'closed' ? `${(t.profit_loss || 0) >= 0 ? '+' : ''}${formatCurrency(t.profit_loss)}` : '-'}
                    </td>
                    <td className="px-5 py-3 text-right text-xs text-[var(--text-secondary)]">{t.opened_at ? new Date(t.opened_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
